import {
  EXTREME_FREE_SPIN_WILD_STOP_WEIGHT_UNITS,
  FREE_SPIN_WILD_STOP_WEIGHT_UNITS,
  REEL_STRIPS,
  REEL_WINDOW_SIZE,
  STOP_WEIGHT_BASE_UNITS,
} from './reel-strips';
import type {
  Grid,
  RandomSource,
  ReelColumn,
  ReelIndex,
  ReelStops,
  ReelSymbolId,
  SpinMode,
} from './types';

export interface WeightedStopProfile {
  readonly weights: readonly number[];
  readonly totalWeight: number;
  readonly favoredStops: readonly number[];
}

export interface GeneratedGrid {
  readonly grid: Grid;
  readonly stops: ReelStops;
}

export type FreeSpinStopProfiles = readonly [
  WeightedStopProfile,
  WeightedStopProfile,
  WeightedStopProfile,
  WeightedStopProfile,
  WeightedStopProfile,
];

export type ReelStripSet = readonly [
  readonly ReelSymbolId[],
  readonly ReelSymbolId[],
  readonly ReelSymbolId[],
  readonly ReelSymbolId[],
  readonly ReelSymbolId[],
];

function assertFiveStrips(
  strips: readonly (readonly ReelSymbolId[])[],
): asserts strips is readonly [
  readonly ReelSymbolId[],
  readonly ReelSymbolId[],
  readonly ReelSymbolId[],
  readonly ReelSymbolId[],
  readonly ReelSymbolId[],
] {
  if (strips.length !== 5) throw new RangeError('Es werden exakt fünf Walzen benötigt.');
  if (strips.some((strip) => strip.length < REEL_WINDOW_SIZE)) {
    throw new RangeError('Jeder Walzenstreifen muss mindestens drei Stops besitzen.');
  }
}

export function normalizeStripPosition(length: number, position: number): number {
  if (!Number.isSafeInteger(length) || length <= 0) {
    throw new RangeError('Die Strip-Länge muss eine positive sichere Ganzzahl sein.');
  }
  if (!Number.isSafeInteger(position)) throw new RangeError('Die Strip-Position muss ganzzahlig sein.');
  return ((position % length) + length) % length;
}

export function getVisibleColumn(
  strip: readonly ReelSymbolId[],
  stopIndex: number,
): ReelColumn {
  if (strip.length < REEL_WINDOW_SIZE) throw new RangeError('Der Strip ist zu kurz.');
  const stop = normalizeStripPosition(strip.length, stopIndex);
  const first = strip[stop];
  const second = strip[(stop + 1) % strip.length];
  const third = strip[(stop + 2) % strip.length];
  if (first === undefined || second === undefined || third === undefined) {
    throw new Error('Der sichtbare Walzenausschnitt ist unvollständig.');
  }
  return Object.freeze([first, second, third]);
}

export function createGridFromStops(
  strips: readonly (readonly ReelSymbolId[])[],
  stops: ReelStops,
): Grid {
  assertFiveStrips(strips);
  stops.forEach((stop, reelIndex) => {
    const stripLength = strips[reelIndex]?.length ?? 0;
    if (!Number.isSafeInteger(stop) || stop < 0 || stop >= stripLength) {
      throw new RangeError(`Stop ${reelIndex + 1} liegt außerhalb des Walzenstreifens.`);
    }
  });
  return Object.freeze([
    getVisibleColumn(strips[0], stops[0]),
    getVisibleColumn(strips[1], stops[1]),
    getVisibleColumn(strips[2], stops[2]),
    getVisibleColumn(strips[3], stops[3]),
    getVisibleColumn(strips[4], stops[4]),
  ]);
}

export function getVisibleStopIndexesForSymbol(
  strip: readonly ReelSymbolId[],
  symbolId: ReelSymbolId,
): readonly number[] {
  const stops = new Set<number>();
  strip.forEach((symbol, symbolIndex) => {
    if (symbol !== symbolId) return;
    for (let row = 0; row < REEL_WINDOW_SIZE; row += 1) {
      stops.add(normalizeStripPosition(strip.length, symbolIndex - row));
    }
  });
  return Object.freeze([...stops].sort((left, right) => left - right));
}

export function buildWeightedStopProfile(
  strip: readonly ReelSymbolId[],
  favoredStops: readonly number[],
  favoredWeight: number,
  normalWeight = STOP_WEIGHT_BASE_UNITS,
): WeightedStopProfile {
  if (!Number.isSafeInteger(favoredWeight) || favoredWeight <= 0) {
    throw new RangeError('Das bevorzugte Stopgewicht muss eine positive sichere Ganzzahl sein.');
  }
  if (!Number.isSafeInteger(normalWeight) || normalWeight <= 0) {
    throw new RangeError('Das normale Stopgewicht muss eine positive sichere Ganzzahl sein.');
  }
  const favored = new Set(favoredStops);
  favored.forEach((stop) => {
    if (!Number.isSafeInteger(stop) || stop < 0 || stop >= strip.length) {
      throw new RangeError('Ein bevorzugter Stop liegt außerhalb des Strips.');
    }
  });
  const weights = Object.freeze(strip.map((_symbol, stop) => (
    favored.has(stop) ? favoredWeight : normalWeight
  )));
  const totalWeight = weights.reduce((sum, weight) => sum + weight, 0);
  if (!Number.isSafeInteger(totalWeight)) throw new RangeError('Das Stopprofil ist zu groß.');
  return Object.freeze({
    weights,
    totalWeight,
    favoredStops: Object.freeze([...favored].sort((left, right) => left - right)),
  });
}

export function pickWeightedStopIndex(profile: WeightedStopProfile, random: RandomSource): number {
  let roll = random.integer(profile.totalWeight);
  for (let stopIndex = 0; stopIndex < profile.weights.length; stopIndex += 1) {
    const weight = profile.weights[stopIndex];
    if (weight === undefined) throw new Error('Stopprofil ist unvollständig.');
    if (roll < weight) return stopIndex;
    roll -= weight;
  }
  throw new Error('Die gewichtete Stopauswahl konnte keinen Stop bestimmen.');
}

export function buildFreeSpinStopProfiles(weightUnits: readonly [number, number, number, number, number]): FreeSpinStopProfiles {
  return Object.freeze(REEL_STRIPS.map((strip, reelIndex) => {
    const reel = reelIndex as ReelIndex;
    return buildWeightedStopProfile(
      strip,
      getVisibleStopIndexesForSymbol(strip, 'WILD'),
      weightUnits[reel],
    );
  })) as FreeSpinStopProfiles;
}

export const FREE_SPIN_STOP_PROFILES = buildFreeSpinStopProfiles(FREE_SPIN_WILD_STOP_WEIGHT_UNITS);
export const EXTREME_FREE_SPIN_STOP_PROFILES = buildFreeSpinStopProfiles(EXTREME_FREE_SPIN_WILD_STOP_WEIGHT_UNITS);

export function selectReelStops(
  mode: SpinMode,
  random: RandomSource,
  freeSpinProfiles: FreeSpinStopProfiles = FREE_SPIN_STOP_PROFILES,
): ReelStops {
  return Object.freeze(REEL_STRIPS.map((strip, reelIndex) => {
    if (mode === 'free') {
      const profile = freeSpinProfiles[reelIndex];
      if (profile === undefined) throw new Error('Freispiel-Stopprofil fehlt.');
      return pickWeightedStopIndex(profile, random);
    }
    return random.integer(strip.length);
  })) as unknown as ReelStops;
}

export function selectUniformReelStops(strips: ReelStripSet, random: RandomSource): ReelStops {
  assertFiveStrips(strips);
  return Object.freeze(strips.map((strip) => random.integer(strip.length))) as unknown as ReelStops;
}

export function createGridFromStrips(strips: ReelStripSet, random: RandomSource): GeneratedGrid {
  const stops = selectUniformReelStops(strips, random);
  return Object.freeze({ grid: createGridFromStops(strips, stops), stops });
}

export function createGrid(
  mode: SpinMode,
  random: RandomSource,
  freeSpinProfiles: FreeSpinStopProfiles = FREE_SPIN_STOP_PROFILES,
): GeneratedGrid {
  const stops = selectReelStops(mode, random, freeSpinProfiles);
  return Object.freeze({ grid: createGridFromStops(REEL_STRIPS, stops), stops });
}
